import { cbSemanticCssVars, typographyCss } from '@creditornot/cb-ingredients';
import React, { CSSProperties } from 'react';
import styled from 'styled-components';

import { WideAreaCheckbox } from 'common/components';
import { useOfferingMenuPermissionsContext } from 'modules/shared-menu/contexts/OfferingMenuPermissionsContext';

import { canSharedVenueUseBulkActions } from './Collections/BulkActions/utils';
import { useSelectionInfoContext } from '../../../contexts/SelectionInfoContext';
import { MENU_MOBILE_BREAKPOINT } from '../constants';
import { useOverrideSettings } from '../hooks/useSelectionOverrideSettings';

type CategorySubheaderProps = {
  style?: CSSProperties;
  className?: string;
  header: string | JSX.Element;
  primaryLanguage: Language;
  onDeselect?: () => void;
  onSelect?: () => void;
  disableSelection?: boolean;
  selectionStatus?: 'selected' | 'partially-selected' | 'not-selected';
};

const Root = styled.div`
  min-height: 64px;
  width: 100%;
  display: flex;
  align-items: center;
`;

const SubHeader = styled.div`
  ${typographyCss.Heading6};
  color: ${cbSemanticCssVars.textPrimary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ContentContainer = styled.div<{ hasMargin: boolean }>`
  flex: 1;
  display: flex;
  justify-content: space-between;
  margin-inline-start: ${({ hasMargin }) => (hasMargin ? '24px' : 0)};
  overflow: hidden;
  @media (max-width: ${MENU_MOBILE_BREAKPOINT}px) {
    margin-inline-start: ${({ hasMargin }) => (hasMargin ? '16px' : 0)};
  }
`;

const StyledWideAreaCheckbox = styled(WideAreaCheckbox)`
  margin-inline-start: 8px;
  @media (max-width: ${MENU_MOBILE_BREAKPOINT}px) {
    margin-inline-start: 0;
  }
`;

const CategorySubheader = ({
  style,
  className,
  header,
  primaryLanguage,
  selectionStatus = 'not-selected',
  onSelect,
  onDeselect,
  disableSelection,
}: CategorySubheaderProps) => {
  const { vlmeConfig } = useSelectionInfoContext();
  const isSharedVenue = !!vlmeConfig?.isSharedVenue;
  const menuPermissions = useOfferingMenuPermissionsContext();
  const { offerAttrsEnabledForOverride } = useOverrideSettings();

  const sharedVenueCanUseBulkActions = canSharedVenueUseBulkActions(offerAttrsEnabledForOverride);
  const bulkActionsEnabled = !!onSelect && !!onDeselect && menuPermissions.contentEdit;

  const canUseBulkActions =
    (bulkActionsEnabled && !isSharedVenue) || (bulkActionsEnabled && sharedVenueCanUseBulkActions);

  return (
    <Root style={style} className={className}>
      {canUseBulkActions && (
        <StyledWideAreaCheckbox
          selectionStatus={selectionStatus}
          onToggle={() => (selectionStatus === 'selected' ? onDeselect() : onSelect())}
          disabled={disableSelection}
        />
      )}
      <ContentContainer hasMargin={!canUseBulkActions}>
        <SubHeader dir="auto" lang={primaryLanguage}>
          {header}
        </SubHeader>
      </ContentContainer>
    </Root>
  );
};

export default React.memo(CategorySubheader);
